import { useEffect, useMemo, useState } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { ColumnDef } from '@tanstack/react-table';
import { useSearchParams } from 'react-router';

import { useAppSelector } from 'src/app/store/hooks';
import { DataGrid } from 'src/common/DataGrid';
import { usePaginationState } from 'src/common/DataGrid/usePaginationState';
import {
  PassengerRead,
  PassengersListApiArg,
  usePassengersListQuery,
} from 'src/common/external/bambi-api/bambiApi';

import { PassengerEmptyState } from './PassengerEmptyState';

interface PassengerDataGridProps {
  columns: ColumnDef<PassengerRead, any>[];
  useDataProvider: typeof usePassengersListQuery;
  searchTermPath?: 'passengerSearchTerm';
}

export function PassengerDataGrid({
  useDataProvider,
  columns,
  searchTermPath = 'passengerSearchTerm',
}: PassengerDataGridProps) {
  const [rowSelection, setRowSelection] = useState({});

  const passengerSearchTerm = useAppSelector(
    (state) => state.passenger[searchTermPath]
  );

  const filterAllowSMSMessage = useAppSelector(
    (state) => state.passenger.filterAllowSMSMessage
  );

  const passengerMergeModalOpen = useAppSelector(
    (state) => state.passenger.passengerMergeModalOpen
  );

  const showDeletedPassengers = useAppSelector(
    (state) => state.passenger.showDeletedPassengers
  );

  const orderBy = useSearchParams()[0].get(
    'ordering'
  ) as PassengersListApiArg['ordering'];

  const allowSmsMessage = useMemo(() => {
    if (filterAllowSMSMessage === 'all') {
      return undefined;
    }
    return filterAllowSMSMessage === 'yes';
  }, [filterAllowSMSMessage]);

  const { pagination, handlePaginationChange, paramsWithPage } =
    usePaginationState<PassengersListApiArg>({
      search: passengerSearchTerm,
      ordering: orderBy,
      allowSmsMessage,
    });

  const {
    data: passengers,
    isLoading,
    refetch,
  } = useDataProvider({
    ...paramsWithPage,
    allowSmsMessage,
    deletedObjects: showDeletedPassengers ? 'include' : undefined,
  });

  useEffect(() => {
    refetch();
  }, [refetch, passengerMergeModalOpen]);

  return (
    <>
      {isLoading ? (
        <div className="my-72 flex justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <div>
            <DataGrid
              tableId="passenger"
              dataTestId="passenger-datagrid"
              columns={columns}
              data={passengers?.results || []}
              pagination={pagination}
              totalCount={passengers?.count || 0}
              handlePaginationChange={handlePaginationChange}
              emptyState={<PassengerEmptyState />}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              getRowId={(row) => row.id}
            />
          </div>
        </>
      )}
    </>
  );
}
